import React, { useEffect, useMemo, useState } from "react";
import {
  EuiAccordion,
  EuiBasicTableColumn,
  EuiButton,
  EuiButtonIcon,
  EuiCallOut,
  EuiCode,
  EuiComboBox,
  EuiComboBoxOptionOption,
  EuiFieldText,
  EuiFlexGroup,
  EuiFlexItem,
  EuiForm,
  EuiFormRow,
  EuiInMemoryTable,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiPanel,
  EuiSearchBar,
  EuiSwitch,
  EuiText,
  EuiTitle,
} from "@elastic/eui";
import { SelectOrInviteMemberModalProps } from "../../../questionnaires/types";
import {
  AnketaActionsEnum,
  AnketaResourcesEnum,
  KeycloakOrganizations,
  KeycloakOrganizationUser,
  KeycloakUserBasis,
} from "../../../auth";
import { useParams } from "react-router";
import { useApi } from "../../../api";
import { useAnketaI18n } from "../../../internationalization";
import { useAnketaAuth } from "../../../auth/context";
import { useToast } from "../../../notifications";

export const SelectOrInviteMemberModal = ({
  onClose,
  isNoModal,
  questionnaireIdOverwrite,
  catalogIdOverwrite,
}: SelectOrInviteMemberModalProps) => {
  const { user, keycloak, checkPermission } = useAnketaAuth();
  const { Get, Post, Delete } = useApi();
  const { translate, currentLang } = useAnketaI18n();
  const { addToast } = useToast();

  const params = useParams();
  const { catalogId, questionnaireId } = params;

  const [organizationMembers, setOrganizationMembers] = useState<
    Array<KeycloakOrganizationUser>
  >([]);

  const [domains, setDomain] = useState<
    Array<
      EuiComboBoxOptionOption<{
        name: string;
        orgId: string;
      }>
    >
  >([]);

  const [membersInQuestionnaire, setMembersInQuestionnaire] = useState<
    Array<string>
  >([]);

  const [callout, setCallout] = useState<JSX.Element | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");

  const [trigger, setTrigger] = useState<number>(0);
  const [showInviteForm, setShowInviteForm] = useState<boolean>(false);

  const [emailAddress, setEmailAddress] = useState<string>("");
  const [selectedDomain, setSelectedDomain] = useState<
    EuiComboBoxOptionOption<{
      name: string;
      orgId: string;
    }>
  >();
  const [surname, setSurname] = useState<string>("");
  const [lastname, setLastname] = useState<string>("");
  const [allOrgs, setAllOrg] = useState<
    Array<
      EuiComboBoxOptionOption<{
        name: string;
        orgId: string;
      }>
    >
  >();
  const [addOrg, setAddOrg] = useState<boolean>(false);

  const canFetch = useMemo<boolean>(() => {
    if (keycloak) {
      if (typeof keycloak.token === "string") {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }, [keycloak]);

  const c = useMemo<string>(() => {
    return catalogIdOverwrite !== undefined ? catalogIdOverwrite : catalogId;
  }, [catalogId, catalogIdOverwrite]);

  const q = useMemo<string>(() => {
    return questionnaireIdOverwrite !== undefined
      ? questionnaireIdOverwrite
      : questionnaireId;
  }, [questionnaireId, questionnaireIdOverwrite]);

  const url = useMemo<string>(() => {
    return `${q}/${c}`;
  }, [c, q]);

  const oldUrl = useMemo<string>(() => {
    return `${c}/${q}`;
  }, [c, q]);

  const addUser = (
    mail: string,
    isNew: boolean,
    firstName: string = "",
    lastName: string = "",
  ) => {
    if (canFetch) {
      Post({
        path: `v2/${AnketaResourcesEnum.questionnaires}/${oldUrl}/members`,
        responseType: "json",
        token: keycloak.token,
        value: {
          usersEmails: [mail],
        },
      })
        .then(() => {
          Post<string>({
            path: `v2/${AnketaResourcesEnum.mail}/${AnketaResourcesEnum.questionnaires}/added`,
            responseType: "json",
            token: keycloak.token,
            value: {
              language: currentLang,
              isNew,
              user: {
                email: mail,
                firstName,
                lastName,
                organizations: "",
              },
              questionnaire: { categoryId: c, questionnaireId: q },
            },
          });
          setCallout(
            <EuiCallOut color="success">
              {translate("anketa.questionnaires.action.add.add.success", {
                values: {
                  mail,
                },
              })}
            </EuiCallOut>,
          );
          setTrigger(trigger + 1);
        })
        .catch((error) => {
          setCallout(
            <EuiCallOut color="danger">
              <EuiCode>
                {typeof error === "string"
                  ? error
                  : error instanceof Error
                    ? error.message
                    : JSON.stringify(error)}
              </EuiCode>
            </EuiCallOut>,
          );
          setTrigger(trigger + 1);
        });
    } else {
      setCallout(
        <EuiCallOut color="danger">
          <EuiText>
            <p>Auth Error</p>
          </EuiText>
        </EuiCallOut>,
      );
    }
  };

  const removeUser = (mail: string) => {
    if (canFetch) {
      Delete({
        path: `v2/${
          AnketaResourcesEnum.questionnaires
        }/${oldUrl}/members?userEmail=${encodeURIComponent(mail)}`,
        responseType: "json",
        token: keycloak.token,
      })
        .then(() => {
          setCallout(
            <EuiCallOut color="success">
              {translate("anketa.questionnaires.action.add.remove.success", {
                values: { mail },
              })}
            </EuiCallOut>,
          );
          setTrigger(trigger + 1);
        })
        .catch((error) => {
          setCallout(
            <EuiCallOut color="danger">
              <EuiCode>
                {typeof error === "string"
                  ? error
                  : error instanceof Error
                    ? error.message
                    : JSON.stringify(error)}
              </EuiCode>
            </EuiCallOut>,
          );
          setTrigger(trigger + 1);
        });
    } else {
      setCallout(
        <EuiCallOut color="danger">
          <EuiText>
            <p>Auth Error</p>
          </EuiText>
        </EuiCallOut>,
      );
    }
  };

  const inviteUser = () => {
    if (canFetch) {
      const organization = selectedDomain.value.orgId;
      const mail = `${emailAddress}@${selectedDomain.value.name}`;
      const inviteUsers: KeycloakUserBasis = {
        email: mail,
        firstName: surname,
        lastName: lastname,
      };

      Post({
        path: `v2/${AnketaResourcesEnum.organizations}/invite`,
        responseType: "json",
        token: keycloak.token,
        value: {
          organizationId: organization,
          user: inviteUsers,
        },
      })
        .then((response) => {
          const { data, success, error } = response;
          if (success) {
            // The backend adds the user even when the Keycloak invitation email
            // could not be sent; it signals this with a `warning` payload so we
            // surface a warning toast instead of silently succeeding.
            if (data && typeof data === "object" && (data as any).warning) {
              addToast({
                title: translate(
                  "questionnaires.action.add.invite.emailFailed.title",
                  { defaultMessage: "Benutzer hinzugefügt - E-Mail fehlgeschlagen" },
                ),
                color: "warning",
                text:
                  (data as any).message ??
                  translate(
                    "questionnaires.action.add.invite.emailFailed.text",
                    {
                      defaultMessage:
                        "Benutzer wurde der Organisation hinzugefügt, aber die Einladungs-E-Mail konnte nicht gesendet werden.",
                    },
                  ),
              });
            }
            addUser(mail, true);
          } else {
            setCallout(
              <EuiCallOut color="danger">
                <EuiCode>
                  {typeof data === "string"
                    ? error
                    : error instanceof Error
                      ? error.message
                      : JSON.stringify(error)}
                </EuiCode>
              </EuiCallOut>,
            );
          }
          closeInviteForm();
        })
        .catch((error) => {
          setCallout(
            <EuiCallOut color="danger">
              <EuiCode>
                {typeof error === "string"
                  ? error
                  : error instanceof Error
                    ? error.message
                    : JSON.stringify(error)}
              </EuiCode>
            </EuiCallOut>,
          );
          closeInviteForm();
        });
    } else {
      setCallout(
        <EuiCallOut color="danger">
          <EuiText>
            <p>Auth Error</p>
          </EuiText>
        </EuiCallOut>,
      );
    }
  };

  const members = useMemo(() => {
    let filteredOrganizationMembers = organizationMembers;

    const domainMatch = searchQuery.match(/domain:([^\s]+)/);
    const domain = domainMatch ? domainMatch[1] : null;

    const freeText = searchQuery.replace(/domain:[^\s]+/, "").trim();

    if (domain) {
      filteredOrganizationMembers = filteredOrganizationMembers.filter(
        (member) => member.email.endsWith(`@${domain}`),
      );
    }

    if (freeText) {
      filteredOrganizationMembers = filteredOrganizationMembers.filter(
        (member) =>
          member.email.toLowerCase()?.includes(freeText.toLowerCase()) ||
          (member.firstName &&
            member.firstName.toLowerCase()?.includes(freeText.toLowerCase())) ||
          (member.lastName &&
            member.lastName.toLowerCase()?.includes(freeText.toLowerCase())),
      );
    }

    const seen = new Set<string>();
    filteredOrganizationMembers = filteredOrganizationMembers.filter(
      (member) => {
        if (seen.has(member.email)) {
          return false;
        }
        seen.add(member.email);
        return true;
      },
    );

    return filteredOrganizationMembers
      .filter((member) => !membersInQuestionnaire?.includes(member.email))
      .map((member) => {
        const fullName =
          member.firstName && member.lastName
            ? `(${member.firstName} ${member.lastName})`
            : "";
        return {
          label: `${member.email} ${fullName}`,
          value: member,
        };
      });
  }, [organizationMembers, searchQuery, membersInQuestionnaire]);

  const columnsQuestionnaireMembers = [
    {
      field: "email",
      name: translate("anketa.user.email"),
      sortable: true,
      truncateText: true,
    },
    {
      name: translate("anketa.questionnaires.action.add.remove"),
      render: (item: KeycloakOrganizationUser) => (
        <EuiButtonIcon
          aria-label={`Remove ${item.email}`}
          iconType="minus"
          color="danger"
          onClick={() => removeUser(item.email)}
        />
      ),
      width: "80px",
    },
  ];

  const columnsOrganizationMembers: Array<
    EuiBasicTableColumn<KeycloakOrganizationUser>
  > = [
    {
      field: "email",
      name: translate("anketa.user.email"),
      sortable: true,
      truncateText: true,
    },
    {
      name: translate("anketa.questionnaires.action.add.add"),
      render: (item: KeycloakOrganizationUser) => (
        <EuiButtonIcon
          aria-label={`${translate("anketa.questionnaires.action.add.add")} ${
            item.email
          }`}
          iconType="plus"
          color="success"
          onClick={() =>
            addUser(item.email, false, item.firstName, item.lastName)
          }
        />
      ),
      width: "80px",
    },
  ];

  const filters = [
    {
      type: "field_value_selection" as const,
      field: "domain",
      name: "Domain",
      multiSelect: false,
      options: domains.map((domain) => ({
        value: domain.label,
        view: domain.label,
      })),
    },
  ];

  const closeInviteForm = () => {
    setEmailAddress("");
    setSurname("");
    setLastname("");
    setShowInviteForm(false);
  };

  const getAllOrgs = async (): Promise<Array<KeycloakOrganizations>> => {
    try {
      const response = await Get<Array<KeycloakOrganizations>>({
        path: `v2/${AnketaResourcesEnum.organizations}`,
        responseType: "json",
        token: keycloak.token,
      });

      const { success, data, error } = response;

      if (success && data) {
        return data;
      } else {
        const errorMessage =
          typeof error === "string"
            ? error
            : error instanceof Error
              ? error.message
              : JSON.stringify(error);

        setCallout(
          <EuiCallOut color="danger" title="Error fetching organizations">
            <EuiCode>{errorMessage}</EuiCode>
          </EuiCallOut>,
        );
        return [];
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : JSON.stringify(err);

      setCallout(
        <EuiCallOut color="danger" title="Network Error">
          <EuiCode>{errorMessage}</EuiCode>
        </EuiCallOut>,
      );
      return [];
    }
  };

  const initData = async () => {
    const orgs = await getAllOrgs();
    if (orgs.length === 0) {
      return;
    }

    const _domains = orgs.flatMap((org) =>
      org.domains.map((domain) => ({
        label: `${domain.name} (${org.name})`,
        value: {
          name: domain.name,
          orgId: org.id,
        },
      })),
    );
    setAllOrg(_domains);
  };
  useEffect(() => {
    if (canFetch) {
      const isInternal = checkPermission(
        AnketaResourcesEnum.questionnaires,
        AnketaActionsEnum.invite,
        {
          isInternal: true,
        },
      );
      if (isInternal) {
        initData();
        Get<Array<KeycloakOrganizations>>({
          path: `v2/${AnketaResourcesEnum.questionnaires}/${oldUrl}/organizations`,
          responseType: "json",
          token: keycloak.token,
        })
          .then((response) => {
            const { success, data, error } = response;
            if (success) {
              const organizations = data.filter(Boolean);
              const availableMembers = organizations
                ?.flatMap((org) => org.members)
                .filter(Boolean);

              const _domains = organizations.flatMap((org) =>
                org.domains.map((domain) => ({
                  label: domain.name,
                  value: {
                    name: domain.name,
                    orgId: org.id,
                  },
                })),
              );
              setDomain(_domains);
              setSelectedDomain(_domains[0]);
              setOrganizationMembers(availableMembers);
            } else {
              setCallout(
                <EuiCallOut color="danger">
                  <EuiCode>
                    {typeof error === "string"
                      ? error
                      : error instanceof Error
                        ? error.message
                        : JSON.stringify(error)}
                  </EuiCode>
                </EuiCallOut>,
              );
            }
          })
          .catch((err) => {
            setCallout(
              <EuiCallOut color="danger">
                <EuiCode>
                  {typeof err === "string"
                    ? err
                    : err instanceof Error
                      ? err.message
                      : JSON.stringify(err)}
                </EuiCode>
              </EuiCallOut>,
            );
          });
      } else {
        Get<Array<KeycloakOrganizations>>({
          path: `v2/${AnketaResourcesEnum.organizations}/my`,
          responseType: "json",
          token: keycloak.token,
        })
          .then((response) => {
            const { success, data, error } = response;
            if (success) {
              const availableMembers = data?.flatMap((organizations) =>
                organizations.members.map((member) => member),
              );
              const _domains = data.flatMap((org) =>
                org.domains.map((domain) => ({
                  label: domain.name,
                  value: {
                    name: domain.name,
                    orgId: org.id,
                  },
                })),
              );
              setDomain(_domains);
              setSelectedDomain(_domains[0]);
              setOrganizationMembers(availableMembers);
            } else {
              /*               setCallout(
                <EuiCallOut color="danger">
                  <EuiCode>
                    {typeof error === "string"
                      ? error
                      : error instanceof Error
                      ? error.message
                      : JSON.stringify(error)}
                  </EuiCode>
                </EuiCallOut>
              ); */
            }
          })
          .catch((err) => {
            console.error(err);
            setCallout(
              <EuiCallOut color="danger">
                <EuiCode>
                  {typeof err === "string"
                    ? err
                    : err instanceof Error
                      ? err.message
                      : JSON.stringify(err)}
                </EuiCode>
              </EuiCallOut>,
            );
          });
      }
      Get<Array<string>>({
        path: `v2/${AnketaResourcesEnum.questionnaires}/${oldUrl}/members`,
        responseType: "json",
        token: keycloak.token,
      })
        .then((response) => {
          const { success, data, error } = response;
          if (success) {
            setMembersInQuestionnaire(data);
          } else {
            setCallout(
              <EuiCallOut color="danger">
                <EuiCode>
                  {typeof error === "string"
                    ? error
                    : error instanceof Error
                      ? error.message
                      : JSON.stringify(error)}
                </EuiCode>
              </EuiCallOut>,
            );
          }
        })
        .catch((err) => {
          setCallout(
            <EuiCallOut color="danger">
              <EuiCode>
                {typeof err === "string"
                  ? err
                  : err instanceof Error
                    ? err.message
                    : JSON.stringify(err)}
              </EuiCode>
            </EuiCallOut>,
          );
        });
    } else {
      setCallout(
        <EuiCallOut color="danger">
          <EuiText>
            <p>Auth Error</p>
          </EuiText>
        </EuiCallOut>,
      );
    }
  }, [c, q, user, trigger, canFetch]);

  if (isNoModal !== undefined && isNoModal) {
    return (
      <EuiPanel paddingSize="l">
        <EuiFlexGroup direction="column" gutterSize="l">
          <EuiFlexItem>
            <EuiTitle>
              <p>
                {translate("anketa.questionnaires.action.add.modal.header")}
              </p>
            </EuiTitle>
          </EuiFlexItem>
          <EuiForm>
            {callout !== null && <EuiFormRow fullWidth>{callout}</EuiFormRow>}
            {showInviteForm ? (
              <EuiFlexItem>
                <EuiFormRow label={translate("anketa.organization.domain")}>
                  <EuiComboBox
                    options={domains}
                    onChange={(
                      options: Array<
                        EuiComboBoxOptionOption<{
                          name: string;
                          orgId: string;
                        }>
                      >,
                    ) => {
                      if (options.length > 0) {
                        setSelectedDomain(options[0]);
                      }
                    }}
                    isClearable
                    singleSelection={{ asPlainText: true }}
                    selectedOptions={selectedDomain ? [selectedDomain] : []}
                  />
                </EuiFormRow>
                <EuiFormRow
                  label={translate("anketa.user.email")}
                  helpText={translate("anketa.user.email.prefix.helper")}
                >
                  <EuiFieldText
                    value={`${emailAddress}`}
                    onChange={(e) => {
                      setEmailAddress(e.target.value);
                    }}
                  />
                </EuiFormRow>
                <EuiFormRow label={translate("anketa.user.surname")}>
                  <EuiFieldText
                    value={surname}
                    onChange={(e) => {
                      setSurname(e.target.value);
                    }}
                  />
                </EuiFormRow>
                <EuiFormRow label={translate("anketa.user.lastname")}>
                  <EuiFieldText
                    value={lastname}
                    onChange={(e) => {
                      setLastname(e.target.value);
                    }}
                  />
                </EuiFormRow>
              </EuiFlexItem>
            ) : (
              <EuiFlexItem>
                <EuiFormRow fullWidth>
                  <EuiFlexGroup>
                    <EuiFlexItem grow={false}>
                      <EuiButton
                        iconType="email"
                        color="secondary"
                        onClick={() => {
                          setShowInviteForm(!showInviteForm);
                        }}
                      >
                        {translate("anketa.questionnaires.action.add.new")}
                      </EuiButton>
                    </EuiFlexItem>
                    <EuiFlexItem grow={true}>
                      <EuiSearchBar
                        query={searchQuery}
                        onChange={({ queryText }) => {
                          setSearchQuery(queryText);
                        }}
                        box={{ placeholder: translate("anketa.table.search") }}
                        filters={filters}
                      />
                    </EuiFlexItem>
                  </EuiFlexGroup>
                </EuiFormRow>
                <EuiFormRow fullWidth>
                  <EuiAccordion
                    id="anketa.questionnaires.existing"
                    buttonContent={translate("anketa.questionnaires.existing")}
                    initialIsOpen={false}
                  >
                    <EuiInMemoryTable
                      items={
                        membersInQuestionnaire
                          ? membersInQuestionnaire.map((email, idx) => ({
                              id: idx.toString(),
                              email,
                            }))
                          : []
                      }
                      columns={columnsQuestionnaireMembers}
                      pagination={false}
                      sorting={true}
                    />
                  </EuiAccordion>
                </EuiFormRow>
                <EuiFormRow fullWidth>
                  <EuiAccordion
                    id="anketa.questionnaires.add"
                    buttonContent={translate("anketa.questionnaires.add")}
                    initialIsOpen={true}
                  >
                    <EuiInMemoryTable
                      items={members.map((member) => member.value)}
                      columns={columnsOrganizationMembers}
                      pagination={false}
                      sorting={true}
                    />
                  </EuiAccordion>
                </EuiFormRow>
              </EuiFlexItem>
            )}
          </EuiForm>
          {showInviteForm && (
            <EuiFlexItem>
              <EuiFlexGroup>
                <EuiFlexItem>
                  <EuiButton
                    color="secondary"
                    iconType="arrowLeft"
                    onClick={() => {
                      closeInviteForm();
                    }}
                  >
                    {translate("anketa.create.action.back")}
                  </EuiButton>
                </EuiFlexItem>
                <EuiFlexItem>
                  <EuiButton
                    color="primary"
                    fill
                    iconType="email"
                    disabled={
                      surname.trim() === "" ||
                      lastname.trim() === "" ||
                      selectedDomain.label.trim() === "" ||
                      emailAddress.trim() === ""
                    }
                    onClick={inviteUser}
                  >
                    {translate("anketa.questionnaires.action.add.invite")}
                  </EuiButton>
                </EuiFlexItem>
              </EuiFlexGroup>
            </EuiFlexItem>
          )}
        </EuiFlexGroup>
      </EuiPanel>
    );
  }
  return (
    <EuiModal
      onClose={() => {
        onClose();
        setSearchQuery("");
        closeInviteForm();
      }}
    >
      <EuiModalHeader>
        <EuiModalHeaderTitle>
          {translate("anketa.questionnaires.action.add.modal.header")}
        </EuiModalHeaderTitle>
      </EuiModalHeader>
      <EuiModalBody>
        <EuiForm>
          {callout !== null && <EuiFormRow fullWidth>{callout}</EuiFormRow>}
          {showInviteForm ? (
            <>
              <EuiFormRow>
                <EuiSwitch
                  label="Add new Organisation"
                  checked={addOrg}
                  onChange={() => {
                    setAddOrg(!addOrg);
                  }}
                />
              </EuiFormRow>
              {addOrg && (
                <EuiFormRow label={translate("anketa.organization.domain")}>
                  <EuiComboBox
                    options={allOrgs}
                    onChange={(
                      options: Array<
                        EuiComboBoxOptionOption<{
                          name: string;
                          orgId: string;
                        }>
                      >,
                    ) => {
                      if (options.length > 0) {
                        setSelectedDomain(options[0]);
                      }
                    }}
                    isClearable
                    singleSelection={{ asPlainText: true }}
                    selectedOptions={selectedDomain ? [selectedDomain] : []}
                  />
                </EuiFormRow>
              )}
              {!addOrg && (
                <EuiFormRow label={translate("anketa.organization.domain")}>
                  <EuiComboBox
                    options={domains}
                    onChange={(
                      options: Array<
                        EuiComboBoxOptionOption<{
                          name: string;
                          orgId: string;
                        }>
                      >,
                    ) => {
                      if (options.length > 0) {
                        setSelectedDomain(options[0]);
                      }
                    }}
                    isClearable
                    singleSelection={{ asPlainText: true }}
                    selectedOptions={selectedDomain ? [selectedDomain] : []}
                  />
                </EuiFormRow>
              )}
              <EuiFormRow
                label={translate("anketa.user.email")}
                helpText={translate("anketa.user.email.prefix.helper")}
              >
                <EuiFieldText
                  value={`${emailAddress}`}
                  onChange={(e) => {
                    setEmailAddress(e.target.value);
                  }}
                />
              </EuiFormRow>
              <EuiFormRow label={translate("anketa.user.surname")}>
                <EuiFieldText
                  value={surname}
                  onChange={(e) => {
                    setSurname(e.target.value);
                  }}
                />
              </EuiFormRow>
              <EuiFormRow label={translate("anketa.user.lastname")}>
                <EuiFieldText
                  value={lastname}
                  onChange={(e) => {
                    setLastname(e.target.value);
                  }}
                />
              </EuiFormRow>
            </>
          ) : (
            <>
              <EuiFormRow fullWidth>
                <EuiFlexGroup>
                  <EuiFlexItem grow={false}>
                    <EuiButton
                      iconType="email"
                      color="secondary"
                      onClick={() => {
                        setShowInviteForm(!showInviteForm);
                      }}
                    >
                      {translate("anketa.questionnaires.action.add.new")}
                    </EuiButton>
                  </EuiFlexItem>
                  <EuiFlexItem grow={true}>
                    <EuiSearchBar
                      query={searchQuery}
                      onChange={({ queryText }) => {
                        setSearchQuery(queryText);
                      }}
                      box={{ placeholder: translate("anketa.table.search") }}
                      filters={filters}
                    />
                  </EuiFlexItem>
                </EuiFlexGroup>
              </EuiFormRow>
              <EuiFormRow fullWidth>
                <EuiAccordion
                  id="anketa.questionnaires.existing"
                  buttonContent={translate("anketa.questionnaires.existing")}
                  initialIsOpen={false}
                >
                  <EuiInMemoryTable
                    items={
                      membersInQuestionnaire
                        ? membersInQuestionnaire.map((email, idx) => ({
                            id: idx.toString(),
                            email,
                          }))
                        : []
                    }
                    columns={columnsQuestionnaireMembers}
                    pagination={false}
                    sorting={true}
                  />
                </EuiAccordion>
              </EuiFormRow>
              <EuiFormRow fullWidth>
                <EuiAccordion
                  id="anketa.questionnaires.add"
                  buttonContent={translate("anketa.questionnaires.add")}
                  initialIsOpen={true}
                >
                  <EuiInMemoryTable
                    items={members.map((member) => member.value)}
                    columns={columnsOrganizationMembers}
                    pagination={false}
                    sorting={true}
                  />
                </EuiAccordion>
              </EuiFormRow>
            </>
          )}
        </EuiForm>
      </EuiModalBody>

      {showInviteForm && (
        <EuiModalFooter>
          <EuiFlexGroup>
            <EuiFlexItem>
              <EuiButton
                color="secondary"
                iconType="arrowLeft"
                onClick={() => {
                  closeInviteForm();
                }}
              >
                {translate("anketa.create.action.back")}
              </EuiButton>
            </EuiFlexItem>
            <EuiFlexItem>
              <EuiButton
                color="primary"
                fill
                iconType="email"
                disabled={
                  surname.trim() === "" ||
                  lastname.trim() === "" ||
                  selectedDomain.label.trim() === "" ||
                  emailAddress.trim() === ""
                }
                onClick={inviteUser}
              >
                {translate("anketa.questionnaires.action.add.invite")}
              </EuiButton>
            </EuiFlexItem>
          </EuiFlexGroup>
        </EuiModalFooter>
      )}
    </EuiModal>
  );
};
